import { addItemsToCart, clearCart } from '../utils';
import { mrOrderIDAttributionKey } from './constants';
import { ErrorSummryVariableToBeConsider } from './data';
import { UploadedDataForTable } from './types';
import { singleProduct } from './utils/useQuickSearchState';

export const getProgressValueByStatus = (status: string) => {
  switch (status) {
    case 'pending':
      return 50;
    case 'uploaded':
      return 75;
    case 'completed':
      return 100;
    default:
      return 0;
  }
};

declare global {
  interface Window {
    upro: {};
  }
}

export const updateWindowUpro = (config: Record<string, any>) => {
  if (window.upro) {
    /* If upro already exists, append the keys/values from the config object */
    Object.assign(window.upro, config);
  } else {
    /* If upro doesn't exist, initialize it with the config values */
    window.upro = {
      ...config,
      name: 'upro',
    };
  }
};

export const orderSummaryDataTransformation = (
  columns: { identifierID: string; columnName: string }[],
  tabledata: Record<string, string>[],
): Record<string, any>[] => {
  const columnMap = new Map(
    columns.map(({ identifierID, columnName }) => [identifierID, columnName]),
  );

  const transformedData = tabledata.map((row) =>
    Object.fromEntries(
      Object.entries(row).map(([key, value]) => [
        columnMap.get(key) ?? key,
        value,
      ]),
    ),
  );

  return transformedData;
};

export const getColumnNameByIdentifier = (
  identifier: string,
  columnsList: { identifier: string; columnName: string }[],
): string | null => {
  const columnMap = new Map(
    columnsList.map((col) => [col.identifier, col.columnName]),
  );
  return columnMap.get(identifier) ?? null;
};

export const getSafeNumber = (obj: any, key: string, defaultValue: number) => {
  return obj?.[key] !== undefined && obj?.[key] !== ''
    ? Number(obj[key])
    : defaultValue;
};

export const getColumnHeadings = (
  tableData: Record<string, string>[],
  columns: {
    columnName: string;
    isShowInOrderSummary?: boolean;
    priority?: number;
  }[],
): string[] => {
  if (!tableData.length) return [];

  const columnMap = new Map(columns.map((col) => [col.columnName, col]));

  const b = Object.keys(tableData[0])
    .filter(
      (key) =>
        columnMap.get(key)?.isShowInOrderSummary ??
        (key !== 'Is Error' && key !== 'Error'),
    )
    .sort(
      (a, b) =>
        (columnMap.get(a)?.priority ?? Infinity) -
        (columnMap.get(b)?.priority ?? Infinity),
    );

  return b;
};

export const getErrorSummaryVariable = (
  columns: {
    identifier: string;
    columnName: string;
    isShowInOrderSummary?: boolean;
    priority?: number;
  }[],
) => {
  if (!columns) return '';

  const columnMap = new Map(columns.map((col) => [col.identifier, col]));

  for (const key of ErrorSummryVariableToBeConsider) {
    if (columnMap.has(key) && columnMap.get(key)?.isShowInOrderSummary) {
      return columnMap.get(key)?.columnName;
    }
  }
  return '';
};

export const getValidUrl = (url: string) => {
  // if path is relative, return as is
  if (url.startsWith('/')) {
    return url;
  }

  // return valid urls as is
  if (/^https?:\/?/i.test(url)) {
    return url;
  }

  // in all other cases
  return `https://${url}`;
};

export const isValueMultipleOfIncrement = (
  value: number,
  incrementValue: number,
) => {
  return value % incrementValue === 0;
};

export const closestMultiples = (increment: number, value: number) => {
  const lowerMultiple = value - (value % increment);
  const upperMultiple =
    lowerMultiple + increment * (value % increment === 0 ? 0 : 1);
  return [lowerMultiple, upperMultiple];
};

interface GetIdsAndQtyParams {
  data: Record<string, string | number>[];
  identifierMap: Record<string, string>;
  columnsTokens?: Record<string, string>;
}
interface GetCreateOrderUrlParams {
  shopURL: string;
  data: Record<string, string | number>[];
  mrOrderId: string;
  isGoToCheckout?: boolean;
  identifierMap?: Record<string, string>;
  columnsTokens?: Record<string, string>;
}

const getIdsAndQty = ({products, columnsTokens}) => {
  console.log('products:', JSON.stringify(products, null, 2));
    if (products.length > 0) {
      const idsAndQty = products
        .filter(
          (elem) => Number(elem[columnsTokens.quantity]) > 0,
        ) /* Filter out elements with Quantity <= 0 */
        .map((elem) => `${elem[columnsTokens.variant_id]}:${elem[columnsTokens.quantity]}`); /* Map to "variantId:quantity" format */
      return idsAndQty.join(',');
    }
    return null;
  };
  // url to redirect when we click on create order button
  export const getCreateOrderUrl = (shopURL: string, products: UploadedDataForTable[] | singleProduct[], columnsTokens: Record<string, string>, mrOrderID: string, isGoToCheckout: boolean) =>
    `http://${shopURL}/cart/${getIdsAndQty({products, columnsTokens})}?attributes[${mrOrderIDAttributionKey}]=${mrOrderID}${!isGoToCheckout ? '&storefront=true' : ''}`;


export const getProductNoteColumnIDs = (columns) => {
    return columns
      .filter(column => column.identifier === 'metafield' && column.showAsProductNote)
      .map(column => ({
        columnName: column.columnName,
        productNoteKey: column.productNoteKey,
      }))
  }

 export const createCart = async (product: UploadedDataForTable[] | singleProduct[], columnTokens: Record<string, string>, columns: {[key: string]: string | number}) => {
    // clear the cart
    await clearCart();

    // create items for cart
    const items = product
      .filter(
        (item) => Number(item[columnTokens.quantity]) > 0,
      ) /* Filter out items with Quantity <= 0 */
      .map((item) => {
        const productNoteColumns = getProductNoteColumnIDs(columns);

        // populating line item properties
        const properties = Object.fromEntries(
          productNoteColumns
            .map(col => [col.productNoteKey, item[col.columnName]])
            .filter(([_, value]) => value) // this removes falsy values
        );

        // for test cases, refer LargeCartPopUp/index.tsx
        return {
          id: Number(item[columnTokens.variant_id]),
          quantity: Number(item[columnTokens.quantity]),
          // only add properties object if it exists
          ...(Object.keys(properties).length > 0 && { properties }),
        };
      });

    const data = await addItemsToCart(items);
    return data ? data : null;
  };